// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import { useEffect, useRef, useState } from "react";
import * as v from "valibot";
import ArrowRight from "~icons/ph/arrow-right";
import CaretLeft from "~icons/ph/caret-left";
import ShieldCheck from "~icons/ph/shield-check";
import { LocaleSchema } from "../../shared/schemas";
import type { AuthenticatedSnapshot } from "../../shared/types";
import { useShell } from "../app/shell";
import {
  useDeleteAccount,
  useSetDiscoverable,
  useSetLastActive,
  useSubmitting,
} from "../data/mutations";
import { useAccount } from "../data/session";
import { number, setLocale, t, useLocale } from "../i18n";
import { NotificationSetting } from "../notifications/attention";
import type { CallNotifications } from "../notifications/use-call-notifications";
import { useConnectionAction } from "../people/person-row";
import { InstallGuide } from "../pwa/install";
import { SoundSettings } from "../sounds/sound-settings";
import { SignInStep } from "./sign-in-settings";
import { FormError, Modal, ProfileAvatar } from "../ui/components";
import { notify } from "../ui/toast";

type Step = "settings" | "sounds" | "sign-in" | "blocked" | "delete";

function SettingsStep({
  account: { me, blocked: blockedPeople },
  notifications,
  returnedFrom,
  setStep,
}: {
  account: AuthenticatedSnapshot;
  notifications: CallNotifications;
  // Coming back from sounds or sign-in returns to the row that opened them.
  returnedFrom: Step;
  setStep: (step: Step) => void;
}) {
  const shell = useShell();
  const locale = useLocale();
  const lastActive = useSetLastActive(),
    discoverable = useSetDiscoverable();
  const submitting = useSubmitting();
  const [error, setError] = useState("");
  const openedFrom = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    openedFrom.current?.focus();
  }, []);
  async function changeLastActive(show: boolean) {
    setError("");
    try {
      await lastActive.mutateAsync(show);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function changeDiscoverable(value: boolean) {
    setError("");
    try {
      await discoverable.mutateAsync(value);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <>
      <h2>{t("الإعدادات")}</h2>
      <InstallGuide
        signedIn
        onSignIn={() => shell.open({ kind: "enter", step: "restore" })}
        onRecovery={() => setStep("sign-in")}
      />
      <NotificationSetting notifications={notifications} />
      <button
        ref={returnedFrom === "sounds" ? openedFrom : undefined}
        type="button"
        className="setting-row setting-link"
        onClick={() => setStep("sounds")}
      >
        <span className="setting-text">
          <strong>{t("أصوات التطبيق")}</strong>
          <small>{t("الرنين والمؤثرات ومستوى الصوت")}</small>
        </span>
        <CaretLeft
          className="directional"
          width={16}
          height={16}
          aria-hidden="true"
        />
      </button>
      <button
        type="button"
        className="setting-row setting-link"
        // One call at a time, and a check would need the microphone too.
        disabled={!!shell.liveCall}
        onClick={shell.startMicrophoneCheck}
      >
        <span className="setting-text">
          <strong>{t("اختبار الميكروفون")}</strong>
          <small>{t("اختبر الصوت واختر أجهزتك")}</small>
        </span>
        <CaretLeft
          className="directional"
          width={16}
          height={16}
          aria-hidden="true"
        />
      </button>
      <label className="setting-row">
        <strong className="setting-text">{t("اللغة")}</strong>
        <select
          value={locale}
          onChange={(e) => setLocale(v.parse(LocaleSchema, e.target.value))}
        >
          <option value="ar" lang="ar">
            العربية
          </option>
          <option value="en" lang="en">
            English
          </option>
        </select>
      </label>
      <label className="setting-row">
        <span className="setting-text">
          <strong id="last-active-label">{t("آخر ظهور وحالة القراءة")}</strong>
          <small id="last-active-help">
            {t(
              "يرى أصدقاؤك متى كنت متصلًا آخر مرة وهل قرأت رسائلهم. يُخفى ذلك عند الظهور كغير متصل.",
            )}
          </small>
        </span>
        <input
          type="checkbox"
          role="switch"
          className="switch"
          checked={me.show_last_active === 1}
          aria-checked={me.show_last_active === 1}
          aria-labelledby="last-active-label"
          aria-describedby="last-active-help"
          disabled={submitting}
          onChange={(e) => void changeLastActive(e.target.checked)}
        />
      </label>
      <label className="setting-row">
        <span className="setting-text">
          <strong id="discoverable-label">
            {t("العثور عليك باسم المستخدم")}
          </strong>
          <small id="discoverable-help">
            {t(
              "يجدك من يكتب اسم المستخدم كاملًا. عند الإيقاف يضيفك الآخرون عبر روابطك فقط.",
            )}
          </small>
        </span>
        <input
          type="checkbox"
          role="switch"
          className="switch"
          checked={me.discoverable === 1}
          aria-checked={me.discoverable === 1}
          aria-labelledby="discoverable-label"
          aria-describedby="discoverable-help"
          disabled={submitting}
          onChange={(e) => void changeDiscoverable(e.target.checked)}
        />
      </label>
      <FormError message={error} />
      <button
        ref={returnedFrom === "sign-in" ? openedFrom : undefined}
        className="setting-row setting-link"
        onClick={() => setStep("sign-in")}
      >
        <span className="setting-text">
          <strong>{t("الدخول والأجهزة")}</strong>
          <small>{t("مفاتيح المرور ورمز الاستعادة وأجهزتك")}</small>
        </span>
        <CaretLeft
          className="directional"
          width={16}
          height={16}
          aria-hidden="true"
        />
      </button>
      <button
        className="setting-row setting-link"
        onClick={() => setStep("blocked")}
      >
        <span className="setting-text">
          <strong>{t("الحسابات المحظورة")}</strong>
        </span>
        <span className="setting-count">{number(blockedPeople.length)}</span>
        <CaretLeft
          className="directional"
          width={16}
          height={16}
          aria-hidden="true"
        />
      </button>
      <button
        className="setting-row setting-link"
        onClick={() => setStep("delete")}
      >
        <span className="setting-text">
          <strong>{t("حذف الحساب")}</strong>
        </span>
        <CaretLeft
          className="directional"
          width={16}
          height={16}
          aria-hidden="true"
        />
      </button>
    </>
  );
}

function BlockedStep({
  blockedPeople,
}: {
  blockedPeople: AuthenticatedSnapshot["blocked"];
}) {
  const shell = useShell();
  const submitting = useSubmitting();
  const requestAction = useConnectionAction();
  return (
    <>
      <h2>{t("الحسابات المحظورة")}</h2>
      <p className="modal-description">
        {t("إلغاء الحظر لا يعيد الصداقة أو طلبات الإضافة.")}
      </p>
      {blockedPeople.length ? (
        blockedPeople.map((p) => (
          <div className="person-row" key={p.id}>
            <ProfileAvatar person={p} dot={false} onOpen={shell.openPerson} />
            <button
              className="person-name"
              aria-label={t("عرض حساب {name}", { name: p.name })}
              onClick={() => shell.openPerson(p)}
            >
              <strong>{p.name}</strong>
              <small dir="ltr">@{p.username}</small>
            </button>
            <button
              className="quiet-button"
              disabled={submitting}
              onClick={() => void requestAction(p, "unblock")}
            >
              {t("إلغاء الحظر")}
            </button>
          </div>
        ))
      ) : (
        <div className="inline-empty">
          <ShieldCheck width={25} height={25} />
          <p>{t("لا توجد حسابات محظورة.")}</p>
        </div>
      )}
    </>
  );
}

function DeleteAccountStep({ onKeep }: { onKeep: () => void }) {
  const { session, close } = useShell();
  const deleteAccount = useDeleteAccount();
  const submitting = useSubmitting();
  const [error, setError] = useState("");
  async function confirm() {
    setError("");
    try {
      await session.end(false);
      await deleteAccount.mutateAsync();
      close();
      notify(t("تم حذف حسابك"), "success");
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <>
      <h2>{t("حذف حسابك نهائيًا؟")}</h2>
      <p className="modal-description">
        {t(
          "ستُحذف رسائلك وأصدقاؤك وسجل مكالماتك ومفاتيح المرور ورمز الاستعادة من رنّة، وتختفي محادثاتك من عند أصدقائك أيضًا. لا يمكن التراجع عن ذلك.",
        )}
      </p>
      <FormError message={error} />
      <div className="confirm-actions">
        <button
          className="button secondary"
          disabled={submitting}
          onClick={onKeep}
        >
          {t("الإبقاء على حسابي")}
        </button>
        <button
          className="button danger-button"
          disabled={submitting}
          onClick={() => void confirm()}
        >
          {t("حذف حسابي نهائيًا")}
        </button>
      </div>
    </>
  );
}

export function SoundsDialog() {
  const { close, liveCall } = useShell();
  return (
    <Modal title={t("أصوات التطبيق")} onClose={close}>
      <h2>{t("أصوات التطبيق")}</h2>
      <SoundSettings inCall={!!liveCall} />
    </Modal>
  );
}

// The account page's sign-in rows come straight here, so Close returns to it.
export function SignInDialog() {
  const { close } = useShell();
  const account = useAccount();
  if (!account) return null;
  return (
    <Modal title={t("الدخول والأجهزة")} onClose={close} className="list-modal">
      <SignInStep account={account} />
    </Modal>
  );
}

// Sounds, sign-in, blocked accounts and deleting the account return to Settings.
export function SettingsDialog({
  notifications,
}: {
  notifications: CallNotifications;
}) {
  const shell = useShell();
  const account = useAccount();
  const [step, setStep] = useState<Step>("settings");
  const [returnedFrom, setReturnedFrom] = useState<Step>("settings");
  if (!account) return null;
  const nested = step !== "settings";
  return (
    <Modal
      key={step}
      title={
        step === "settings"
          ? t("الإعدادات")
          : step === "sounds"
            ? t("أصوات التطبيق")
            : step === "sign-in"
              ? t("الدخول والأجهزة")
              : step === "blocked"
                ? t("الحسابات المحظورة")
                : t("حذف حسابك نهائيًا؟")
      }
      onClose={
        nested
          ? () => {
              setReturnedFrom(step);
              setStep("settings");
            }
          : shell.close
      }
      closeLabel={nested ? t("رجوع") : undefined}
      closeIcon={
        nested ? (
          <ArrowRight className="directional" width={19} height={19} />
        ) : undefined
      }
      className={step === "blocked" || step === "sign-in" ? "list-modal" : ""}
    >
      {step === "settings" && (
        <SettingsStep
          account={account}
          notifications={notifications}
          returnedFrom={returnedFrom}
          setStep={setStep}
        />
      )}
      {step === "sounds" && (
        <>
          <h2>{t("أصوات التطبيق")}</h2>
          <SoundSettings inCall={!!shell.liveCall} />
        </>
      )}
      {step === "sign-in" && <SignInStep account={account} />}
      {step === "blocked" && <BlockedStep blockedPeople={account.blocked} />}
      {step === "delete" && (
        <DeleteAccountStep onKeep={() => setStep("settings")} />
      )}
    </Modal>
  );
}
